import { API } from '@ids-services';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Guid } from 'guid-typescript';
import { Observable, Subscriber, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { IFirmwareService } from './IFirmwareService';

const API_FIRMWARE = `${API.FOTA_MANAGER}/firmware`;

@Injectable({
  providedIn: 'root',
})
export class FirmwareService implements IFirmwareService {
  constructor(private http: HttpClient) {}

  getFirmwares(project_id?: any, device_id?: any) {
    const params: string[] = [];
    if (!!project_id) {
      params.push(`project_id=${project_id}`);
    }
    if (!!device_id) {
      params.push(`device_id=${device_id}`);
    }
    return this.http
      .get<any>(`${API_FIRMWARE}${!!params.length ? `?${params.join('&')}` : ''}`)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getFirmware(id: any) {
    return this.http.get<any>(`${API_FIRMWARE}/${id}?show_device=true`).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getDeviceFirmwareStatus(device_id: any) {
    return this.http
      .get<any>(`${API.FOTA_MANAGER}/${device_id}/firmware_status`)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  uploadFirmware(payload: any) {
    const observer = new Observable((observe) => {
      const requests: Observable<any>[] = [];
      const chunkFiles: any[] = [];
      const chunkSize = 10000000;

      const file = payload.file;
      for (let offset = 0; offset < file.size; offset += chunkSize) {
        const chunkFile = file.slice(offset, offset + chunkSize);
        chunkFile.lastModified = file.lastModified;
        chunkFile.lastModifiedDate = file.lastModifiedDate;
        chunkFile.name = `${file.name}.${Guid.create()}`;
        chunkFiles.push(chunkFile);
      }
      const dzuuid = Guid.create();
      chunkFiles.forEach((chunkFile: any, index: number) => {
        const chunkPayload = {
          dzuuid,
          dzchunkindex: index,
          dztotalchunkcount: chunkFiles.length,
          file: chunkFile,
          target_device: payload.target_device,
          name: payload.name,
          version: payload.version,
          signed: +payload.signed,
          project_id: payload.project_id,
          org_id: payload.org_id,
        };
        requests.push(this.upload(chunkPayload));
      });
      this.uploadChunks(requests, 0, observe);
    });
    return observer;
  }

  private upload(payload: any) {
    const formData = new FormData();
    formData.append('dzuuid', payload.dzuuid);
    formData.append('dzchunkindex', payload.dzchunkindex);
    formData.append('dztotalchunkcount', payload.dztotalchunkcount);
    formData.append('file', payload.file, payload.file?.name);
    formData.append('target_device', payload.target_device);
    formData.append('name', payload.name);
    formData.append('version', payload.version);
    formData.append('signed', payload.signed);
    formData.append('project_id', payload.project_id);
    formData.append('org_id', payload.org_id);
    return this.http.post<any>(`${API_FIRMWARE}/upload`, formData).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  private uploadChunks(chunkRequests: Observable<any>[], index: number, observe: Subscriber<any>) {
    chunkRequests[index].subscribe({
      next: () => {
        if (index < chunkRequests.length - 1) {
          this.uploadChunks(chunkRequests, index + 1, observe);
        } else {
          observe.next();
        }
      },
      error: (err: any) => {
        observe.error(err);
      },
    });
  }

  updateFirmware(id: any, payload: any) {
    return this.http.put<any>(`${API_FIRMWARE}/${id}`, payload).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  deleteFirmware(id: any, organizationId: any) {
    return this.http
      .delete<any>(`${API_FIRMWARE}/${id}?org_id=${organizationId}`)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  downloadFirmware(id: any) {
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.append('Content-Type', 'application/octet-stream');
    return this.http
      .get(`${API_FIRMWARE}/${id}/download`, {
        headers,
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  pushHikVisionCCTVDevice(id: any, payload: any) {
    return this.http.post(`${API_FIRMWARE}/${id}/fota`, payload).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getSupportedDevices(): Observable<any> {
    return this.http.get<any>(`${API_FIRMWARE}/supported_devices`).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }
}
